import React from 'react';

const LoadingSpinner = ({ size = 'md' }) => <span className={`loading loading-spinner loading-${size} text-primary`}></span>;

const OverallStatsCard = ({
    selectedDays,
    onDaysButtonClick,
    overallStats,
    selectedClassifications,
    onClassificationFilterClick,
    onClearClassificationFilters, // Expects a function like () => setSelectedClassifications(new Set())
    isLoadingSnippetsAndStats,
    isLoadingPlayers,
    displayedPlayersCount, // Pass the count instead of the whole array
    isMobile = false,
}) => {

    const dateOptions = [
        { label: '1D', value: 1 }, { label: '3D', value: 3 }, { label: '7D', value: 7 },
        { label: '30D', value: 30 }, { label: 'All', value: 0 },
    ];

    const hasClassifications = overallStats.classification_counts && Object.keys(overallStats.classification_counts).length > 0;

    return (
        <div className={`card bg-base-100 shadow-md p-4 ${isMobile ? 'h-60 overflow-y-auto' : ''}`}>
            <div className={`flex flex-col ${isMobile ? 'gap-2' : 'sm:flex-row justify-between items-start sm:items-center gap-2'} mb-4`}>
                <h3 className="text-md md:text-lg font-semibold">Overall Stats</h3>
                <div className="btn-group">
                    {dateOptions.map(day => (
                        <button 
                            key={day.value} 
                            className={`btn btn-xs ${isMobile ? '' : 'sm:btn-sm'} ${selectedDays === day.value ? 'btn-active btn-primary' : 'btn-ghost'}`}
                            onClick={() => onDaysButtonClick(day.value)} 
                            disabled={isLoadingSnippetsAndStats || isLoadingPlayers}
                        >
                            {day.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoadingSnippetsAndStats && displayedPlayersCount > 0 ? (
                <div className="text-center p-4"><LoadingSpinner /></div>
            ) : (
                <>
                    <div className={`${isMobile ? 'mb-2' : 'mb-4'}`}>
                        <h4 className={`text-sm ${isMobile ? 'mb-1' : 'md:text-md font-medium mb-2'} text-accent`}>Sentiment Overview</h4>
                        <div className={`stats ${isMobile ? 'stats-horizontal shadow w-full bg-base-200 text-xs' : 'stats-vertical sm:stats-horizontal shadow w-full bg-base-200 text-xs sm:text-sm'}`}>
                            <div className={`stat ${isMobile ? 'p-1' : 'p-2 sm:p-4'}`}><div className={`stat-title ${isMobile ? 'text-[0.6rem] leading-tight' : ''}`}>Tot</div><div className={`stat-value ${isMobile ? 'text-sm' : 'text-lg sm:text-xl'}`}>{overallStats.total_snippets ?? 0}</div></div>
                            <div className={`stat ${isMobile ? 'p-1' : 'p-2 sm:p-4'}`}><div className={`stat-title text-success ${isMobile ? 'text-[0.6rem] leading-tight' : ''}`}>Pos</div><div className={`stat-value text-success ${isMobile ? 'text-sm' : 'text-lg sm:text-xl'}`}>{overallStats.positive_count ?? 0}</div></div>
                            <div className={`stat ${isMobile ? 'p-1' : 'p-2 sm:p-4'}`}><div className={`stat-title text-error ${isMobile ? 'text-[0.6rem] leading-tight' : ''}`}>Neg</div><div className={`stat-value text-error ${isMobile ? 'text-sm' : 'text-lg sm:text-xl'}`}>{overallStats.negative_count ?? 0}</div></div>
                            <div className={`stat ${isMobile ? 'p-1' : 'p-2 sm:p-4'}`}><div className={`stat-title ${isMobile ? 'text-[0.6rem] leading-tight' : ''}`}>Net</div><div className={`stat-value ${isMobile ? 'text-sm' : 'text-lg sm:text-xl'} ${overallStats.net_score > 0 ? 'text-success' : overallStats.net_score < 0 ? 'text-error' : ''}`}>{overallStats.net_score ?? 0}</div></div>
                        </div>
                    </div>
                    <div className={`${isMobile ? 'mt-2' : 'mt-6'}`}>
                        <h4 className={`text-sm ${isMobile ? 'mb-1' : 'md:text-md font-medium mb-2'} text-accent`}>Classification Filter</h4>
                        {!hasClassifications ? (
                            <p className="text-xs italic text-base-content/70">
                                {displayedPlayersCount > 0 ? "No classified snippets." : "Select players to see classifications."}
                            </p>
                        ) : (
                            <div className={`flex flex-wrap items-center ${isMobile ? 'gap-1' : 'gap-2'}`}> 
                                {Object.entries(overallStats.classification_counts)
                                    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                                    .map(([classification, count]) => (
                                    <button
                                        key={classification}
                                        onClick={() => onClassificationFilterClick(classification)}
                                        className={`indicator transition-all duration-150 ease-in-out transform hover:scale-105 ${selectedClassifications.has(classification) ? (isMobile ? 'ring-1 ring-primary ring-offset-1 ring-offset-base-100 rounded-md' : 'ring-2 ring-primary ring-offset-2 ring-offset-base-100 rounded-lg') : ''}`}
                                        title={`${selectedClassifications.has(classification) ? 'Remove' : 'Filter by'} ${classification}`}
                                        disabled={isLoadingSnippetsAndStats || isLoadingPlayers}
                                    >
                                        <span className={`indicator-item indicator-top indicator-end badge badge-xs ${isMobile ? '' : 'sm:badge-sm'} font-semibold ${selectedClassifications.has(classification) ? 'badge-neutral' : 'badge-secondary'}`}>{count}</span>
                                        <div className={`grid place-items-center py-0.5 px-1.5 ${isMobile ? 'text-[0.7rem] rounded-md' : 'sm:px-3 rounded-lg text-xs sm:text-sm'} shadow ${selectedClassifications.has(classification) ? 'bg-primary text-primary-content' : 'bg-base-300'}`}>
                                            {classification}
                                        </div>
                                    </button>
                                ))}
                                {selectedClassifications.size > 0 && (
                                    <button
                                        onClick={onClearClassificationFilters} 
                                        className={`btn btn-xs btn-ghost text-error hover:bg-error/10 ${isMobile ? 'ml-1 py-0.5 px-1 text-[0.7rem]' : 'ml-2'}`} 
                                        title="Clear all classification filters"
                                        disabled={isLoadingSnippetsAndStats || isLoadingPlayers}
                                    >
                                        {isMobile ? 'Clear' : 'Clear Filters'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default OverallStatsCard; 